import { createAsyncThunk } from '@reduxjs/toolkit'
import axios from '../../../config/axios'
import { Event, CreateEventPayload, User } from './types'

export const fetchEvents = createAsyncThunk<Event[]>(
  'manageEvents/fetchEvents',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/events')
      return response.data
    } catch (error) {
      return rejectWithValue('Failed to fetch events')
    }
  },
)

export const createEvent = createAsyncThunk<Event, CreateEventPayload>(
  'manageEvents/createEvent',
  async (eventData) => {
    try {
      const formData = new FormData()

      // Append all fields to FormData
      Object.entries(eventData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'imageFile' && value instanceof File) {
            formData.append(key, value)
          } else if (key === 'startDate' || key === 'endDate') {
            formData.append(key, value instanceof Date ? value.toISOString() : value)
          } else if (key === 'createdBy') {
            formData.append('createdBy.email', value.email)
            formData.append('createdBy.workdayId', value.workdayId)
          } else if (typeof value === 'object') {
            formData.append(key, JSON.stringify(value))
          } else {
            formData.append(key, value.toString())
          }
        }
      })

      const response = await axios.post('/events/createEvent', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data.event
    } catch (error: any) {
      console.error('Failed to create event:', error.response?.data || error.message)
      throw error // Forward error to thunk's error handling
    }
  },
)

export const updateEvent = createAsyncThunk(
  'manageEvents/updateEvent',
  async (payload: { id: number; updatedBy: User; imageFile?: File } & Partial<Event>) => {
    const { id, updatedBy, imageFile, ...data } = payload

    const formData = new FormData()

    formData.append('id', id.toString())

    // Handle nested objects and format dates
    const appendFormData = (obj: any, prefix = '') => {
      for (const key in obj) {
        if (obj[key] !== undefined && obj[key] !== null) {
          const fieldName = prefix ? `${prefix}.${key}` : key

          if (
            typeof obj[key] === 'object' &&
            !(obj[key] instanceof File) &&
            !(obj[key] instanceof Date)
          ) {
            // Recursively handle nested objects
            appendFormData(obj[key], fieldName)
          } else {
            // Handle regular fields
            formData.append(fieldName, obj[key].toString())
          }
        }
      }
    }

    // Append all data
    appendFormData(data)

    // Handle file separately
    if (imageFile) {
      formData.append('imageFile', imageFile)
    }

    // Append update metadata
    appendFormData({ updatedBy }, 'updatedBy')

    try {
      const response = await axios.patch('/events/updateEvent', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data.event
    } catch (error: any) {
      console.error('Failed to update event:', error.response?.data || error.message)
      throw error // Forward error to thunk's error handling
    }
  },
)

export const fetchEventDetails = createAsyncThunk<Event, string>(
  'manageEvents/fetchEventDetails',
  async (eventId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/events/eventDetails/${eventId}`)
      return response.data
    } catch (error) {
      return rejectWithValue('Failed to fetch event details')
    }
  },
)

export const updateInvitedTeamMember = createAsyncThunk(
  'event/updateInvitedTeamMember',
  async (payload: any, { rejectWithValue }) => {
    const { teamMemberEmail, teamMemberWorkdayId, eventId, status, address } = payload

    const queryParams = new URLSearchParams()
    if (eventId) queryParams.append('eventId', eventId.toString())
    if (teamMemberEmail) queryParams.append('teamMemberEmail', teamMemberEmail)
    if (teamMemberWorkdayId) queryParams.append('teamMemberWorkdayId', teamMemberWorkdayId)

    const formData = new FormData()
    if (status) formData.append('status', status)
    if (address) formData.append('address', address)

    try {
      const response = await axios.patch(`/events/updateInvitedTeamMember?${queryParams}`, formData, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      return response.data
    } catch (error: any) {
      console.error('Error updating team member event:', error)
      return rejectWithValue(error.response?.data || error.message)
    }
  },
)
